import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function Hero() {
  const heroRef = useRef(null);
  const carRef = useRef(null);
  const wheelsRef = useRef([]);
  const cloudRef = useRef(null);
  const mountainsRef = useRef(null);
  const treesRef = useRef(null);
  const roadLinesRef = useRef(null);
  const headlineRef = useRef(null);

  useEffect(() => {
    // INITIAL LOAD ANIMATIONS
    const tl = gsap.timeline({ delay: 0.3 });
    
    // Animate headline - fade in + slide up with stagger on each letter
    tl.fromTo(
      ".headline-letter",
      { opacity: 0, y: 50, scale: 0.8 },
      { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        duration: 0.8,
        stagger: 0.05,
        ease: "power3.out"
      }
    );

    // Animate statistics - stagger one by one
    tl.fromTo(
      ".stat-item",
      { opacity: 0, y: 30, scale: 0.9 },
      { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        duration: 0.6,
        stagger: 0.15,
        ease: "back.out(1.2)"
      },
      "-=0.4"
    );

    // Animate scroll indicator
    tl.fromTo(
      ".scroll-indicator",
      { opacity: 0, y: -20 },
      { opacity: 1, y: 0, duration: 0.5 },
      "-=0.2"
    );

    // Continuous wheel rotation
    gsap.to(".wheel", {
      rotation: "+=360",
      duration: 1,
      repeat: -1,
      ease: "none"
    });

    // SCROLL-DRIVEN ANIMATION - Car drives across screen
    const scrollTL = gsap.timeline({
      scrollTrigger: {
        trigger: heroRef.current,
        start: "top top",
        end: "bottom top",
        scrub: 1,
      },
    });

    scrollTL
      // Car moves from left to right across entire screen
      .to(
        carRef.current,
        {
          x: window.innerWidth + 300, // Move completely off right side
          y: -30, // Slight upward movement
          rotationZ: -2, // Lean forward
          ease: "none",
        },
        0
      )
      // Clouds move slower (parallax)
      .to(
        cloudRef.current,
        {
          x: -400,
          ease: "none",
        },
        0
      )
      // Mountains move medium speed
      .to(
        mountainsRef.current,
        {
          x: -600,
          ease: "none",
        },
        0
      )
      // Trees move faster (closer to camera)
      .to(
        treesRef.current,
        {
          x: -1000,
          ease: "none",
        },
        0
      )
      // Road lines move fastest
      .to(
        roadLinesRef.current,
        {
          x: -1500,
          ease: "none",
        },
        0
      );

    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="h-screen w-full flex items-center justify-center overflow-hidden relative"
      style={{
        background: 'linear-gradient(180deg, #87CEEB 0%, #E0F6FF 50%, #FFE9D0 100%)'
      }}
    >
      {/* Sun */}
      <div className="absolute top-20 right-32 w-32 h-32 bg-yellow-300 rounded-full shadow-2xl shadow-yellow-400/50" style={{
        boxShadow: '0 0 100px 40px rgba(255, 223, 0, 0.4)'
      }} />

      {/* Clouds Layer (Slowest) */}
      <div ref={cloudRef} className="absolute inset-0 pointer-events-none" style={{ width: '200%' }}>
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full opacity-80"
            style={{
              width: `${120 + Math.random() * 80}px`,
              height: `${60 + Math.random() * 40}px`,
              left: `${i * 25}%`,
              top: `${10 + Math.random() * 30}%`,
              boxShadow: '0 10px 30px rgba(255,255,255,0.5)',
            }}
          >
            <div className="absolute w-2/3 h-2/3 bg-white rounded-full -left-6 top-2" />
            <div className="absolute w-1/2 h-1/2 bg-white rounded-full -right-4 top-3" />
          </div>
        ))}
      </div>

      {/* Mountains Layer (Medium-Slow) */}
      <div ref={mountainsRef} className="absolute bottom-0 left-0 pointer-events-none" style={{ width: '300%', height: '60%' }}>
        {/* Back mountains */}
        <svg viewBox="0 0 1200 400" className="absolute bottom-0 w-full h-full">
          <defs>
            <linearGradient id="mountain1" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#8B7355" />
              <stop offset="100%" stopColor="#A0826D" />
            </linearGradient>
            <linearGradient id="mountain2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#6B5B4A" />
              <stop offset="100%" stopColor="#8B7355" />
            </linearGradient>
          </defs>
          
          {/* Far mountains */}
          <path d="M 0 300 L 200 150 L 400 200 L 600 100 L 800 180 L 1000 120 L 1200 250 L 1200 400 L 0 400 Z" 
            fill="url(#mountain1)" opacity="0.6" />
          
          {/* Mid mountains */}
          <path d="M 0 350 L 150 220 L 350 280 L 550 180 L 750 240 L 950 200 L 1200 300 L 1200 400 L 0 400 Z" 
            fill="url(#mountain2)" opacity="0.8" />
        </svg>
      </div>

      {/* Trees Layer (Medium-Fast) */}
      <div ref={treesRef} className="absolute bottom-0 left-0 pointer-events-none" style={{ width: '400%', height: '40%' }}>
        <div className="absolute bottom-0 w-full h-full flex items-end gap-20">
          {[...Array(30)].map((_, i) => (
            <div key={i} className="flex-shrink-0" style={{ marginLeft: i * 60 }}>
              {/* Tree */}
              <div className="relative">
                {/* Trunk */}
                <div className="w-6 h-24 bg-gradient-to-b from-amber-800 to-amber-900 mx-auto rounded-sm" />
                {/* Leaves - triangle */}
                <div className="absolute -top-16 left-1/2 transform -translate-x-1/2">
                  <div className="w-0 h-0 border-l-[30px] border-l-transparent border-r-[30px] border-r-transparent border-b-[40px] border-b-green-700" />
                  <div className="w-0 h-0 border-l-[35px] border-l-transparent border-r-[35px] border-r-transparent border-b-[45px] border-b-green-600 absolute top-6 left-1/2 transform -translate-x-1/2" />
                  <div className="w-0 h-0 border-l-[40px] border-l-transparent border-r-[40px] border-r-transparent border-b-[50px] border-b-green-500 absolute top-12 left-1/2 transform -translate-x-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Road */}
      <div className="absolute bottom-0 w-full h-1/4 bg-gradient-to-b from-gray-600 to-gray-700 overflow-hidden">
        {/* Road lines */}
        <div ref={roadLinesRef} className="absolute top-1/2 transform -translate-y-1/2 flex gap-12" style={{ width: '400%' }}>
          {[...Array(60)].map((_, i) => (
            <div
              key={i}
              className="w-16 h-2 bg-yellow-400 rounded-full"
            />
          ))}
        </div>
        
        {/* Road edge */}
        <div className="absolute top-0 w-full">
          <div className="w-full h-1 bg-gray-500" />
        </div>
      </div>

      {/* Car (Drives across screen) */}
      <div
        ref={carRef}
        className="absolute z-30 will-change-transform"
        style={{
          left: '-15%',
          bottom: '28%',
          transform: 'scale(1.8)',
        }}
      >
        <div className="relative" style={{ width: '280px', height: '120px' }}>
          {/* Speed lines */}
          <div className="absolute -left-32 top-1/2 transform -translate-y-1/2 opacity-80">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="absolute bg-gradient-to-r from-transparent via-yellow-400 to-transparent rounded-full animate-pulse"
                style={{
                  width: `${100 + i * 20}px`,
                  height: '4px',
                  top: `${i * 15}px`,
                  animationDelay: `${i * 0.1}s`,
                  opacity: 0.7 - i * 0.1
                }}
              />
            ))}
          </div>

          {/* Headlight glow */}
          <div className="absolute right-0 top-1/2 w-40 h-10 bg-yellow-100/60 blur-2xl transform -translate-y-1/2" />
          
          {/* Car SVG */}
          <svg
            viewBox="0 0 280 120"
            className="w-full h-full"
            style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.6))' }}
          >
            {/* Shadow */}
            <ellipse cx="140" cy="112" rx="110" ry="10" fill="rgba(0,0,0,0.5)" />
            
            {/* Modern Racing Car - BLACK */}
            <g>
              {/* Under body / Side skirt */}
              <path d="M 40 100 L 50 92 L 230 92 L 245 100 Z" fill="#0A0A0A" opacity="0.9" />
              
              {/* Main body - sleek aerodynamic shape */}
              <path d="M 35 85 L 45 75 L 240 75 L 255 82 L 255 95 L 35 95 Z" fill="url(#racingCarGradient)" stroke="#FCD34D" strokeWidth="2.5" />
              
              {/* Racing stripe - GOLD/YELLOW */}
              <rect x="45" y="80" width="195" height="5" fill="#FCD34D" opacity="0.9" />
              
              {/* Front bumper/splitter */}
              <path d="M 245 88 L 260 86 L 265 91 L 255 93 Z" fill="#000" stroke="#FCD34D" strokeWidth="1.5" />
              
              {/* Side air vent */}
              <g>
                <rect x="140" y="82" width="25" height="6" rx="2" fill="#0A0A0A" opacity="0.8" />
                <line x1="143" y1="82" x2="143" y2="88" stroke="#FCD34D" strokeWidth="0.8" opacity="0.7" />
                <line x1="148" y1="82" x2="148" y2="88" stroke="#FCD34D" strokeWidth="0.8" opacity="0.7" />
                <line x1="153" y1="82" x2="153" y2="88" stroke="#FCD34D" strokeWidth="0.8" opacity="0.7" />
                <line x1="158" y1="82" x2="158" y2="88" stroke="#FCD34D" strokeWidth="0.8" opacity="0.7" />
              </g>
              
              {/* Cabin / Windshield */}
              <path d="M 80 75 L 105 45 L 175 45 L 195 75 Z" fill="url(#windshieldGradient)" stroke="#FCD34D" strokeWidth="2" />
              
              {/* Window reflection */}
              <path d="M 110 52 L 120 60 L 160 60 L 170 52 Z" fill="rgba(100,200,255,0.3)" />
              
              {/* Side mirror */}
              <ellipse cx="195" cy="70" rx="6" ry="4" fill="#0A0A0A" stroke="#FCD34D" strokeWidth="1.5" />
              
              {/* Rear spoiler - bigger and more aggressive */}
              <g>
                <rect x="35" y="65" width="4" height="15" fill="#0A0A0A" stroke="#FCD34D" strokeWidth="0.5" />
                <rect x="42" y="65" width="4" height="15" fill="#0A0A0A" stroke="#FCD34D" strokeWidth="0.5" />
                <path d="M 28 60 L 50 60 L 48 68 L 30 68 Z" fill="url(#spoilerGradient)" stroke="#FCD34D" strokeWidth="2" />
              </g>
              
              {/* Headlights - LED style */}
              <g>
                <rect x="248" y="82" width="8" height="3" rx="1" fill="#F0F9FF" opacity="0.95">
                  <animate attributeName="opacity" values="0.95;1;0.95" dur="2s" repeatCount="indefinite" />
                </rect>
                <rect x="248" y="87" width="8" height="3" rx="1" fill="#EFF6FF" opacity="0.9">
                  <animate attributeName="opacity" values="0.9;1;0.9" dur="2s" repeatCount="indefinite" />
                </rect>
              </g>
              
              {/* Tail lights - LED style */}
              <g>
                <rect x="38" y="82" width="6" height="3" rx="1" fill="#DC2626" opacity="0.9" />
                <rect x="38" y="87" width="6" height="3" rx="1" fill="#EF4444" opacity="0.8" />
              </g>
              
              {/* Exhaust pipes */}
              <circle cx="50" cy="96" r="3" fill="#1A1A1A" stroke="#FCD34D" strokeWidth="1" />
              <circle cx="60" cy="96" r="3" fill="#1A1A1A" stroke="#FCD34D" strokeWidth="1" />
              
              {/* Racing number */}
              <text x="100" y="72" fontFamily="Arial Black" fontSize="16" fill="#FCD34D" fontWeight="bold" stroke="#000" strokeWidth="0.5">47</text>
            </g>

            <defs>
              <linearGradient id="racingCarGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#1A1A1A" />
                <stop offset="50%" stopColor="#0A0A0A" />
                <stop offset="100%" stopColor="#000000" />
              </linearGradient>
              <linearGradient id="windshieldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#2A2A2A" />
                <stop offset="100%" stopColor="#0A0A0A" />
              </linearGradient>
              <linearGradient id="spoilerGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#1A1A1A" />
                <stop offset="100%" stopColor="#000000" />
              </linearGradient>
            </defs>
          </svg>

          {/* Wheels */}
          <div className="absolute bottom-1 left-12">
            <div
              ref={(el) => (wheelsRef.current[0] = el)}
              className="wheel w-12 h-12 rounded-full bg-gradient-to-br from-gray-900 to-black border-3 shadow-2xl relative"
              style={{ borderColor: '#FCD34D' }}
            >
              <div className="absolute inset-1 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                <div className="w-2 h-2 rounded-full" style={{ background: '#FCD34D' }} />
              </div>
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="absolute top-1/2 left-1/2 w-0.5 h-full"
                  style={{
                    transform: `translate(-50%, -50%) rotate(${i * 72}deg)`,
                    background: '#FCD34D'
                  }}
                />
              ))}
            </div>
            {/* Dust cloud from back wheel */}
            <div className="absolute -left-8 top-1/2 transform -translate-y-1/2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="absolute bg-gray-500 rounded-full animate-pulse"
                  style={{
                    width: `${20 + i * 10}px`,
                    height: `${20 + i * 10}px`,
                    left: `-${i * 15}px`,
                    opacity: 0.35 - i * 0.1,
                    animationDelay: `${i * 0.2}s`,
                  }}
                />
              ))}
            </div>
          </div>

          <div className="absolute bottom-1 right-12">
            <div
              ref={(el) => (wheelsRef.current[1] = el)}
              className="wheel w-12 h-12 rounded-full bg-gradient-to-br from-gray-900 to-black border-3 shadow-2xl relative"
              style={{ borderColor: '#FCD34D' }}
            >
              <div className="absolute inset-1 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                <div className="w-2 h-2 rounded-full" style={{ background: '#FCD34D' }} />
              </div>
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="absolute top-1/2 left-1/2 w-0.5 h-full"
                  style={{
                    transform: `translate(-50%, -50%) rotate(${i * 72}deg)`,
                    background: '#FCD34D'
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Text Overlay */}
      <div className="absolute top-32 left-1/2 transform -translate-x-1/2 z-40 text-center pointer-events-none">
        {/* Headline with letter spacing */}
        <h1 
          ref={headlineRef}
          className="font-black mb-8"
          style={{
            fontSize: '80px',
            color: '#000000',
            textShadow: '4px 4px 0px rgba(255,255,255,0.5), -2px -2px 0 #fff, 2px -2px 0 #fff, -2px 2px 0 #fff, 2px 2px 0 #fff, 0 0 30px rgba(255,255,255,0.6)',
            letterSpacing: '0.3em',
            lineHeight: '1.2'
          }}
        >
          {['W', 'E', 'L', 'C', 'O', 'M', 'E'].map((letter, i) => (
            <span key={i} className="headline-letter inline-block">{letter}</span>
          ))}
          <br />
          {['I', 'T', 'Z', 'F', 'I', 'Z', 'Z'].map((letter, i) => (
            <span key={i + 7} className="headline-letter inline-block">{letter}</span>
          ))}
        </h1>
      </div>

      {/* Statistics - Left Side Vertical */}
      <div className="absolute left-8 top-20 z-40 flex flex-col gap-12 pointer-events-none">
        {/* Stat 1 */}
        <div className="stat-item text-left">
          <div 
            className="text-5xl font-black mb-2"
            style={{
              color: '#DC2626',
              textShadow: '3px 3px 0px rgba(0,0,0,0.2), 0 0 20px rgba(220, 38, 38, 0.4), -1px -1px 0 #fff, 1px -1px 0 #fff'
            }}
          >
            95%
          </div>
          <p className="text-lg font-bold" style={{ 
            color: '#1F2937',
            textShadow: '1px 1px 2px rgba(255,255,255,0.9)'
          }}>
            Success Rate
          </p>
        </div>

        {/* Stat 2 */}
        <div className="stat-item text-left">
          <div 
            className="text-5xl font-black mb-2"
            style={{
              color: '#DC2626',
              textShadow: '3px 3px 0px rgba(0,0,0,0.2), 0 0 20px rgba(220, 38, 38, 0.4), -1px -1px 0 #fff, 1px -1px 0 #fff'
            }}
          >
            1000+
          </div>
          <p className="text-lg font-bold" style={{ 
            color: '#1F2937',
            textShadow: '1px 1px 2px rgba(255,255,255,0.9)'
          }}>
            Clients
          </p>
        </div>

        {/* Stat 3 */}
        <div className="stat-item text-left">
          <div 
            className="text-5xl font-black mb-2"
            style={{
              color: '#DC2626',
              textShadow: '3px 3px 0px rgba(0,0,0,0.2), 0 0 20px rgba(220, 38, 38, 0.4), -1px -1px 0 #fff, 1px -1px 0 #fff'
            }}
          >
            50+
          </div>
          <p className="text-lg font-bold" style={{ 
            color: '#1F2937',
            textShadow: '1px 1px 2px rgba(255,255,255,0.9)'
          }}>
            Countries
          </p>
        </div>

        {/* Stat 4 */}
        <div className="stat-item text-left">
          <div 
            className="text-5xl font-black mb-2"
            style={{
              color: '#DC2626',
              textShadow: '3px 3px 0px rgba(0,0,0,0.2), 0 0 20px rgba(220, 38, 38, 0.4), -1px -1px 0 #fff, 1px -1px 0 #fff'
            }}
          >
            10+
          </div>
          <p className="text-lg font-bold" style={{ 
            color: '#1F2937',
            textShadow: '1px 1px 2px rgba(255,255,255,0.9)'
          }}>
            Years
          </p>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="scroll-indicator absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2 z-40 animate-bounce">
        <div className="w-8 h-12 border-3 rounded-full flex items-start justify-center p-2" style={{ borderColor: '#1F2937', background: 'rgba(255,255,255,0.3)' }}>
          <div className="w-2 h-4 rounded-full" style={{ background: '#1F2937' }} />
        </div>
        <p className="text-base font-bold" style={{ color: '#1F2937', textShadow: '1px 1px 2px rgba(255,255,255,0.8)' }}>↓ SCROLL HERE ↓</p>
      </div>

      {/* Creator Name */}
      <div className="absolute bottom-6 right-8 z-40">
        <p className="text-lg font-bold tracking-wider" style={{ 
          color: '#1F2937',
          textShadow: '2px 2px 4px rgba(255,255,255,0.9), -1px -1px 0 #fff, 1px -1px 0 #fff'
        }}>
          JYOTHI SAMBERPU
        </p>
      </div>
    </section>
  );
}